import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

export interface PurposeCode {
  id?: number;
  purposeCode: string;
  useCase: string;
  mccs?: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
}

export interface Mcc {
  mcc: string;
  description: string;
}

export interface MccDetail extends Mcc {
  purposeCodes: Array<Pick<PurposeCode, "purposeCode" | "useCase">>;
}

interface PurposeCodeState {
  loading: boolean;
  error: string | null;
  purposeDetailLoading: boolean;
  purposeDetailError: string | null;
  mccLoading: boolean;
  mccError: string | null;
  mccDetailLoading: boolean;
  mccDetailError: string | null;
  upsertLoading: boolean;
  upsertError: string | null;
  mappingUpdateLoading: boolean;
  mappingUpdateError: string | null;
  successMessage: string | null;
  purposeCodes: PurposeCode[];
  selectedPurposeCode: PurposeCode | null;
  mccs: Mcc[];
  selectedMcc: MccDetail | null;
}

const initialState: PurposeCodeState = {
  loading: false,
  error: null,
  purposeDetailLoading: false,
  purposeDetailError: null,
  mccLoading: false,
  mccError: null,
  mccDetailLoading: false,
  mccDetailError: null,
  upsertLoading: false,
  upsertError: null,
  mappingUpdateLoading: false,
  mappingUpdateError: null,
  successMessage: null,
  purposeCodes: [],
  selectedPurposeCode: null,
  mccs: [],
  selectedMcc: null,
};

export const fetchPurposeCodes = createAsyncThunk(
  "purposeCode/fetchPurposeCodes",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/purpose-codes`,
        timeout: 10000,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data.purposeCodes as PurposeCode[];
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch purpose codes",
      );
    }
  },
);

export const fetchPurposeCodeByCode = createAsyncThunk(
  "purposeCode/fetchPurposeCodeByCode",
  async (code: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/purpose-codes/${encodeURIComponent(code)}`,
        timeout: 10000,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data.purposeCode as PurposeCode;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch purpose code",
      );
    }
  },
);

export const fetchMccs = createAsyncThunk(
  "purposeCode/fetchMccs",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/mccs`,
        timeout: 10000,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data.mccs as Mcc[];
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch MCCs",
      );
    }
  },
);

export const fetchMccByCode = createAsyncThunk(
  "purposeCode/fetchMccByCode",
  async (mcc: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/mccs/${encodeURIComponent(mcc)}`,
        timeout: 10000,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data.mcc as MccDetail;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch MCC details",
      );
    }
  },
);

export const upsertPurposeCode = createAsyncThunk(
  "purposeCode/upsertPurposeCode",
  async (
    payload: { code: string; useCase: string; mccs: Record<string, string> },
    { rejectWithValue },
  ) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/purpose-codes/${encodeURIComponent(payload.code)}`,
        timeout: 10000,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: {
          useCase: payload.useCase,
          mccs: payload.mccs,
        },
      });
      return response.data.purposeCode as PurposeCode;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to save purpose code",
      );
    }
  },
);

export const addPurposeCodeMccMapping = createAsyncThunk(
  "purposeCode/addPurposeCodeMccMapping",
  async (payload: { code: string; mcc: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");

      // First, create the mapping
      await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/purpose-codes/${encodeURIComponent(payload.code)}/mccs`,
        timeout: 10000,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: {
          mcc: payload.mcc,
        },
      });

      // Then refetch the updated purpose code so UI stays in sync
      const purposeCodeResponse = await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/purpose-codes/${encodeURIComponent(payload.code)}`,
        timeout: 10000,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return purposeCodeResponse.data.purposeCode as PurposeCode;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to add MCC mapping for purpose code",
      );
    }
  },
);

export const removePurposeCodeMccMapping = createAsyncThunk(
  "purposeCode/removePurposeCodeMccMapping",
  async (payload: { code: string; mcc: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");

      await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/purpose-codes/${encodeURIComponent(payload.code)}/mccs/${encodeURIComponent(payload.mcc)}`,
        timeout: 10000,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Refetch updated purpose code
      const purposeCodeResponse = await axios.request({
        baseURL: `${window.env.API_URL}/v1/utility/purpose-codes/${encodeURIComponent(payload.code)}`,
        timeout: 10000,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return purposeCodeResponse.data.purposeCode as PurposeCode;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to remove MCC mapping for purpose code",
      );
    }
  },
);

const purposeCodeSlice = createSlice({
  name: "purposeCode",
  initialState,
  reducers: {
    clearPurposeCodeMessages(state) {
      state.error = null;
      state.purposeDetailError = null;
      state.mccError = null;
      state.mccDetailError = null;
      state.upsertError = null;
      state.mappingUpdateError = null;
      state.successMessage = null;
    },
    clearSelectedPurposeCode(state) {
      state.selectedPurposeCode = null;
      state.purposeDetailError = null;
    },
    clearSelectedMcc(state) {
      state.selectedMcc = null;
      state.mccDetailError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPurposeCodes.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPurposeCodes.fulfilled,
        (state, action: PayloadAction<PurposeCode[]>) => {
          state.loading = false;
          state.purposeCodes = action.payload;
        },
      )
      .addCase(fetchPurposeCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPurposeCodeByCode.pending, state => {
        state.purposeDetailLoading = true;
        state.purposeDetailError = null;
      })
      .addCase(
        fetchPurposeCodeByCode.fulfilled,
        (state, action: PayloadAction<PurposeCode>) => {
          state.purposeDetailLoading = false;
          state.selectedPurposeCode = action.payload;
        },
      )
      .addCase(fetchPurposeCodeByCode.rejected, (state, action) => {
        state.purposeDetailLoading = false;
        state.purposeDetailError = action.payload as string;
      })
      .addCase(fetchMccs.pending, state => {
        state.mccLoading = true;
        state.mccError = null;
      })
      .addCase(fetchMccs.fulfilled, (state, action: PayloadAction<Mcc[]>) => {
        state.mccLoading = false;
        state.mccs = action.payload;
      })
      .addCase(fetchMccs.rejected, (state, action) => {
        state.mccLoading = false;
        state.mccError = action.payload as string;
      })
      .addCase(fetchMccByCode.pending, state => {
        state.mccDetailLoading = true;
        state.mccDetailError = null;
      })
      .addCase(
        fetchMccByCode.fulfilled,
        (state, action: PayloadAction<MccDetail>) => {
          state.mccDetailLoading = false;
          state.selectedMcc = action.payload;
        },
      )
      .addCase(fetchMccByCode.rejected, (state, action) => {
        state.mccDetailLoading = false;
        state.mccDetailError = action.payload as string;
      })
      .addCase(upsertPurposeCode.pending, state => {
        state.upsertLoading = true;
        state.upsertError = null;
        state.successMessage = null;
      })
      .addCase(
        upsertPurposeCode.fulfilled,
        (state, action: PayloadAction<PurposeCode>) => {
          state.upsertLoading = false;
          state.successMessage = "Purpose code saved successfully";
          state.selectedPurposeCode = action.payload;
          const idx = state.purposeCodes.findIndex(
            code => code.purposeCode === action.payload.purposeCode,
          );
          if (idx >= 0) {
            state.purposeCodes[idx] = action.payload;
          } else {
            state.purposeCodes = [...state.purposeCodes, action.payload];
          }
        },
      )
      .addCase(upsertPurposeCode.rejected, (state, action) => {
        state.upsertLoading = false;
        state.upsertError = action.payload as string;
      })
      .addCase(addPurposeCodeMccMapping.pending, state => {
        state.mappingUpdateLoading = true;
        state.mappingUpdateError = null;
      })
      .addCase(
        addPurposeCodeMccMapping.fulfilled,
        (state, action: PayloadAction<PurposeCode>) => {
          state.mappingUpdateLoading = false;
          state.selectedPurposeCode = action.payload;
          const idx = state.purposeCodes.findIndex(
            code => code.purposeCode === action.payload.purposeCode,
          );
          if (idx >= 0) {
            state.purposeCodes[idx] = action.payload;
          }
        },
      )
      .addCase(addPurposeCodeMccMapping.rejected, (state, action) => {
        state.mappingUpdateLoading = false;
        state.mappingUpdateError = action.payload as string;
      })
      .addCase(removePurposeCodeMccMapping.pending, state => {
        state.mappingUpdateLoading = true;
        state.mappingUpdateError = null;
      })
      .addCase(
        removePurposeCodeMccMapping.fulfilled,
        (state, action: PayloadAction<PurposeCode>) => {
          state.mappingUpdateLoading = false;
          state.selectedPurposeCode = action.payload;
          const idx = state.purposeCodes.findIndex(
            code => code.purposeCode === action.payload.purposeCode,
          );
          if (idx >= 0) {
            state.purposeCodes[idx] = action.payload;
          }
        },
      )
      .addCase(removePurposeCodeMccMapping.rejected, (state, action) => {
        state.mappingUpdateLoading = false;
        state.mappingUpdateError = action.payload as string;
      });
  },
});

export const {
  clearPurposeCodeMessages,
  clearSelectedPurposeCode,
  clearSelectedMcc,
} =
  purposeCodeSlice.actions;

export default purposeCodeSlice.reducer;
